import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Eyebrow, Reveal, Rich } from "./Motion";
import { api } from "../lib/api";

const TILE_W = "w-[240px] sm:w-[270px] lg:w-[300px]";

const Tile = ({ c, i, visit }) => (
  <div className={`${TILE_W} shrink-0 pr-3 lg:pr-4`}>
    <div data-testid={`client-tile-${c.name.toLowerCase().replace(/[^a-z]/g, "").slice(0, 10)}-${i}`} className="group relative h-full aspect-[4/3] border border-[rgba(16,16,20,0.12)] bg-[var(--bone)] p-6 flex flex-col overflow-hidden">
      <span className="absolute inset-0 bg-[#101014] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)]" />
      <div className="relative flex-1 grid place-items-center">
        {c.logo_url ? (
          <img src={c.logo_url} alt={c.name} className="max-h-16 max-w-[70%] object-contain grayscale group-hover:grayscale-0 group-hover:brightness-110 transition-[filter] duration-500" />
        ) : (
          <span className="display text-xl lg:text-2xl text-center group-hover:text-[var(--bone)] transition-colors duration-300">{c.name}</span>
        )}
      </div>
      <div className="relative flex items-end justify-between gap-2 mono text-[10.5px] uppercase">
        <div className="min-w-0">
          {c.sector && <span className="block truncate text-[rgba(16,16,20,0.7)] group-hover:text-[rgba(246,241,232,0.85)] transition-colors duration-300">{c.sector}</span>}
          {(c.market || c.country) && <span data-testid={`client-location-${i}`} className="block mt-1 text-[var(--fux)] group-hover:text-[var(--fux-2)] font-semibold transition-colors duration-300">→ {c.market || c.country}</span>}
        </div>
        {c.website && (
          <a data-testid={`client-link-${i}`} href={c.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#b08d1f] group-hover:text-[var(--amber-2)] shrink-0">{visit}<ArrowUpRight className="w-3 h-3" /></a>
        )}
      </div>
    </div>
  </div>
);

const Track = ({ items, visit }) => {
  const reps = items.length >= 6 ? 2 : 3;
  const half = Array.from({ length: reps }, () => items).flat();
  return (
    <div data-testid="clients-band" className="marquee-wrap overflow-hidden">
      <div className="marquee-track" style={{ animationDuration: items.length >= 6 ? "50s" : "60s" }}>
        {[...half, ...half].map((c, i) => <Tile key={`${c.id}-${i}`} c={c} i={i % items.length} visit={visit} />)}
      </div>
    </div>
  );
};

const Group = ({ title, items, visit }) => (
  <div className="mb-14 last:mb-0">
    <Reveal><h3 data-testid="clients-group-title" className="display text-2xl lg:text-4xl tracking-[-0.01em] mb-6"><Rich text={title} /></h3></Reveal>
    <Track items={items} visit={visit} />
  </div>
);

export const Clients = () => {
  const { t } = useLang();
  const [clients, setClients] = useState([]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => setClients([]));
  }, []);
  const cons = clients.filter((c) => c.category !== "import");
  const imp = clients.filter((c) => c.category === "import");
  return (
    <section id="clients" data-testid="clients-section" className="relative bg-[var(--bone)] text-[var(--ink)] py-24 lg:py-40">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 mb-14 lg:mb-20">
          <div className="lg:col-span-7">
            <Eyebrow className="!text-[#b08d1f] [&>span]:!bg-[#b08d1f]">{t.clients.eyebrow}</Eyebrow>
            <Reveal><h2 data-testid="clients-title" className="mt-8 display text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-[-0.01em]"><Rich text={t.clients.title} /></h2></Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-4 lg:col-start-9 self-end"><p className="text-base lg:text-lg text-[rgba(16,16,20,0.75)] leading-relaxed">{t.clients.intro}</p></Reveal>
        </div>
        {cons.length > 0 ? (
          <Group title={t.clients.consTitle} items={cons} visit={t.clients.visit} />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 border-l border-t border-[rgba(16,16,20,0.12)] mb-14">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} data-testid={`client-placeholder-${i}`} className="aspect-[4/3] border-r border-b border-[rgba(16,16,20,0.12)] grid place-items-center">
                <span className="mono text-[12px] uppercase text-[rgba(16,16,20,0.55)] border border-dashed border-[rgba(16,16,20,0.3)] px-4 py-3">{t.clients.placeholder}</span>
              </div>
            ))}
          </div>
        )}
        {imp.length > 0 && <Group title={t.clients.impTitle} items={imp} visit={t.clients.visit} />}
      </div>
    </section>
  );
};
